import { useQuery } from 'react-query'
import { useMemo } from 'react'
import { Source } from '@allara-health/source-health-client'
import { Logger } from 'utils/logger.utils'
import { Action } from 'hooks/useTimeline/useActions'
import { AppointmentBookingFlowType } from 'containers/Dashboard/MyHealth/PastAppointments/AppointmentNoteModal'
import { AppointmentNote } from 'hooks/useAppointments/useAppointmentNotes'

interface AppointmentTypeDataInterface {
  followupAppointmentTypeId: string | null
  appointmentPlanningHorizon: number
  isLoading: boolean
  error: Error | null
}

/**
 * Moves code inside useEffect from AppointmentNoteModal inside this hook that leverages useQuery. The purpose of this hook
 * is to determine the appointment type id and appointment planning horizon, which then is used
 * to determine the legacy conditions to display the Book Next Visit CTA in the appointment note.
 */
const useAppointmentTypeData = (
  note: AppointmentNote | null,
  appointmentBookingFlow: AppointmentBookingFlowType,
  activeActions: Action[],
  source: Source
): AppointmentTypeDataInterface => {
  const logger = new Logger('AppointmentNoteModal')
  const appointmentTypeId = useMemo(() => {
    if (!note?.followup || !appointmentBookingFlow) {
      return null
    }

    // get the appointment type id of the pending actions related to 'book'
    const definitionsIds = activeActions.filter(
      (action) =>
        action.definition.includes('book') &&
        action.definition.includes(appointmentBookingFlow)
    )

    return definitionsIds[0]?.properties?.typeId || null
  }, [note?.followup, appointmentBookingFlow, activeActions])

  const { data: appointmentTypeData, isLoading, error, isError } = useQuery({
    queryKey: ['appointmentType', appointmentTypeId],
    queryFn: async () => {
      if (!appointmentTypeId) {
        return null
      }

      const apptType = await source.scheduling.appointmentTypes.retrieve(
        appointmentTypeId
      )
      return apptType
    },
    enabled: !!appointmentTypeId, // Only fetch if we have an ID
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    onError: (error: Error) => {
      logger.error('Error fetching appointment type:', error.message)
    },
  })

  return {
    followupAppointmentTypeId: appointmentTypeId,
    appointmentPlanningHorizon: appointmentTypeData?.planning_horizon ?? 0,
    isLoading,
    error: isError ? error : null,
  }
}

export default useAppointmentTypeData
